import { ComponentType, FormEvent, SVGProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { GatewayAPIError } from "../../app/api/client";
import { authStore } from "../auth/authStore";
import { ContactsPanel } from "../contacts/ContactsPanel";
import { DiscoveryPanel } from "../discovery/DiscoveryPanel";
import { removeProfileAvatar, replaceProfileAvatar, SelfProfile, getSelfProfile } from "../profile/profileApi";
import { ProfileAvatarPanel } from "../profile/ProfileAvatarPanel";
import { runtimeI18n } from "../i18n/i18n";
import {
  ConversationListResponse,
  ConversationSummary,
  createChannelConversation,
  createDirectConversation,
  createGroupConversation,
  createSecretConversation,
  listConversations,
  markConversationDelivered,
  readActorUserIDFromAccessToken,
  resolveUserIDByIdentifier,
  uploadAvatarMedia,
} from "./chatApi";
import { RealtimeEvent, connectRealtime } from "./realtimeClient";
import {
  collectDirectPeerUserIDs,
  describeDirectPeerAvatar,
  describeConversationWithDisplayName,
  isAvatarReferenceDirectlyRenderable,
  resolveUserDisplayNames,
} from "./userDisplayNames";
import {
  IconBroadcast,
  IconClose,
  IconLock,
  IconLogout,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSend,
  IconUser,
  IconUsers,
} from "./chatIcons";

type ChatCreateType = "direct" | "group" | "channel" | "secret";

interface ChatTypeOption {
  key: ChatCreateType;
  labelKey: string;
  noteKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const CHAT_TYPE_OPTIONS: ChatTypeOption[] = [
  {
    key: "direct",
    labelKey: "chatList.chatTypes.direct.label",
    noteKey: "chatList.chatTypes.direct.note",
    icon: IconUser,
  },
  {
    key: "group",
    labelKey: "chatList.chatTypes.group.label",
    noteKey: "chatList.chatTypes.group.note",
    icon: IconUsers,
  },
  {
    key: "channel",
    labelKey: "chatList.chatTypes.channel.label",
    noteKey: "chatList.chatTypes.channel.note",
    icon: IconBroadcast,
  },
  {
    key: "secret",
    labelKey: "chatList.chatTypes.secret.label",
    noteKey: "chatList.chatTypes.secret.note",
    icon: IconLock,
  },
];

export interface ConversationRowText {
  primary: string;
  secondary: string;
}

function isSameLocalDate(first: Date, second: Date): boolean {
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

export function resolveConversationTimestampSource(conversation: ConversationSummary): string {
  const updatedAt = conversation.updated_at?.trim() ?? "";
  if (updatedAt !== "") {
    return updatedAt;
  }
  return conversation.created_at;
}

export function formatConversationTimestamp(value: string, now: Date = new Date()): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  if (isSameLocalDate(parsed, now)) {
    return parsed.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export function resolveConversationRowText(
  conversation: ConversationSummary,
  actorUserID: string | null,
): ConversationRowText {
  const displayLabel = describeConversationWithDisplayName(conversation, actorUserID);
  const collaborativeDescription = conversation.description?.trim() ?? "";
  if ((conversation.type === "group" || conversation.type === "channel") && collaborativeDescription !== "") {
    return {
      primary: collaborativeDescription,
      secondary: displayLabel,
    };
  }

  return {
    primary: displayLabel,
    secondary: describeConversationPreview(conversation),
  };
}

function looksLikeUserID(value: string): boolean {
  return value.startsWith("usr_");
}

function normalizeIdentifierList(identifiers: string[]): string[] {
  const unique = new Set<string>();
  for (const identifier of identifiers) {
    const normalized = identifier.trim();
    if (normalized !== "") {
      unique.add(normalized);
    }
  }
  return [...unique];
}

const COLLABORATIVE_NAME_MAX_LENGTH = 64;
const COLLABORATIVE_DESCRIPTION_MAX_LENGTH = 280;

function collaborativeCreateErrorMessage(error: GatewayAPIError, type: "group" | "channel"): string {
  switch (error.code) {
    case "not_found":
      return runtimeI18n.t(type === "group" ? "chatList.errors.groupMembersNotFound" : "chatList.errors.channelPublishersNotFound");
    case "invalid_request":
      return runtimeI18n.t(
        type === "group" ? "chatList.errors.groupInvalidRequest" : "chatList.errors.channelInvalidRequest",
      );
    case "conflict":
      return runtimeI18n.t(type === "group" ? "chatList.errors.groupConflict" : "chatList.errors.channelConflict");
    case "forbidden":
      return runtimeI18n.t(type === "group" ? "chatList.errors.groupForbidden" : "chatList.errors.channelForbidden");
    default:
      return runtimeI18n.t(type === "group" ? "chatList.errors.groupCreateFallback" : "chatList.errors.channelCreateFallback");
  }
}

export function validateCollaborativeName(name: string): string | null {
  const normalized = name.trim();
  if (normalized === "") {
    return runtimeI18n.t("chatList.validation.nameRequired");
  }
  if (/\s/.test(normalized)) {
    return runtimeI18n.t("chatList.validation.nameOneWord");
  }
  if (normalized.length > COLLABORATIVE_NAME_MAX_LENGTH) {
    return runtimeI18n.t("chatList.validation.nameMax");
  }
  return null;
}

export function validateCollaborativeDescription(description: string): string | null {
  const normalized = description.trim();
  if (normalized.includes("\n") || normalized.includes("\r")) {
    return runtimeI18n.t("chatList.validation.descriptionSingleLine");
  }
  if (normalized.length > COLLABORATIVE_DESCRIPTION_MAX_LENGTH) {
    return runtimeI18n.t("chatList.validation.descriptionMax");
  }
  return null;
}

export function canSubmitCollaborativeCreate(
  name: string,
  description: string,
  participantIdentifiers: string[],
): boolean {
  return (
    validateCollaborativeName(name) === null &&
    validateCollaborativeDescription(description) === null &&
    normalizeIdentifierList(participantIdentifiers).length > 0
  );
}

export function canSubmitSecretCreate(identifier: string): boolean {
  return identifier.trim() !== "";
}

function normalizeConversationAvatarAssetID(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

function appendConversationAvatarContext(path: string, conversationID: string | null | undefined): string {
  const normalizedConversationID = conversationID?.trim() ?? "";
  if (normalizedConversationID === "") {
    return path;
  }
  const query = new URLSearchParams();
  query.set("conversation_id", normalizedConversationID);
  query.set("purpose", "conversation_avatar");
  return `${path}?${query.toString()}`;
}

function resolveConversationAvatarSourceKey(conversationID: string, assetID: string): string {
  const normalizedConversationID = conversationID.trim();
  const normalizedAssetID = assetID.trim();
  if (normalizedConversationID === "") {
    return normalizedAssetID;
  }
  return `${normalizedConversationID}::${normalizedAssetID}`;
}

export function resolveConversationAvatarAssetPath(
  value: string | null | undefined,
  conversationID?: string | null,
): string | null {
  const assetID = normalizeConversationAvatarAssetID(value);
  if (assetID === "") {
    return null;
  }
  return appendConversationAvatarContext(`/v1/media/assets/${encodeURIComponent(assetID)}/binary`, conversationID);
}

export function mapSecretCreateErrorMessage(error: GatewayAPIError): string {
  switch (error.code) {
    case "not_found":
      return runtimeI18n.t("chatList.errors.secretUserNotFound");
    case "invalid_request":
      return runtimeI18n.t("chatList.errors.secretInvalidIdentifier");
    case "forbidden":
      return runtimeI18n.t("chatList.errors.secretForbidden");
    default:
      return runtimeI18n.t("chatList.errors.secretCreateFallback");
  }
}

export function mapCollaborativeAvatarUploadErrorMessage(error: unknown): string {
  if (error instanceof GatewayAPIError) {
    switch (error.code) {
      case "not_found":
        return runtimeI18n.t("chatList.errors.avatarNotFound");
      case "conflict":
        return runtimeI18n.t("chatList.errors.avatarProcessing");
      case "invalid_request":
        return runtimeI18n.t("chatList.errors.avatarInvalid");
      default:
        return runtimeI18n.t("chatList.errors.avatarUploadFallback");
    }
  }
  return runtimeI18n.t("chatList.errors.avatarUploadFallback");
}

export const CHAT_LIST_SHELL_CLASSES = {
  root: "chat-shell chat-shell-list",
  rail: "conversation-rail",
  profilePanel: "profile-avatar-panel",
  composerLane: "composer-lane composer-lane-inline",
  startTypeMenu: "start-chat-type-menu",
  list: "conversation-list",
  item: "conversation-item",
} as const;

function deriveConversationInitial(value: string): string {
  const source = value.trim();
  if (source === "") {
    return "#";
  }
  return source[0]?.toUpperCase() ?? "#";
}

function normalizeSequence(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.trunc(value));
}

export function normalizeConversationUnreadCount(
  conversation: Pick<ConversationSummary, "unread_count">,
): number {
  return normalizeSequence(conversation.unread_count);
}

export function formatConversationUnreadBadge(count: number): string {
  const normalized = normalizeSequence(count);
  if (normalized >= 100) {
    return "99+";
  }
  return String(normalized);
}

interface ConversationListRealtimeResult {
  conversations: ConversationSummary[];
  shouldRefresh: boolean;
  removedChatID?: string;
}

export interface RealtimeDeliveredReceiptClaim {
  chatID: string;
  sequence: number;
}

export function claimRealtimeDeliveredReceipt(
  event: RealtimeEvent,
  actorUserID: string | null,
  claimed: Set<string>,
): RealtimeDeliveredReceiptClaim | null {
  if (event.type !== "message.created") {
    return null;
  }
  const actor = actorUserID?.trim() ?? "";
  if (actor === "") {
    return null;
  }
  const senderUserID = event.sender_user_id?.trim() ?? "";
  if (senderUserID === "" || senderUserID === actor) {
    return null;
  }
  const chatID = event.chat_id.trim();
  const sequence = normalizeSequence(event.sequence);
  if (chatID === "" || sequence <= 0) {
    return null;
  }

  const key = `${chatID}:${sequence}`;
  if (claimed.has(key)) {
    return null;
  }
  claimed.add(key);
  return { chatID, sequence };
}

function updateConversationReadState(
  conversation: ConversationSummary,
  latestSequence: number,
  readSequence: number,
): ConversationSummary {
  const nextLatestSequence = Math.max(normalizeSequence(conversation.latest_sequence), latestSequence);
  const nextReadSequence = Math.max(0, Math.min(nextLatestSequence, readSequence));
  return {
    ...conversation,
    latest_sequence: nextLatestSequence,
    read_sequence: nextReadSequence,
    unread_count: Math.max(0, nextLatestSequence - nextReadSequence),
  };
}

function isValidLifecycleEvent<TLifecycleState extends "closed" | "removed">(
  event: RealtimeEvent,
  lifecycleState: TLifecycleState,
): event is RealtimeEvent & {
  actor_user_id: string;
  conversation_type: "group" | "channel";
  lifecycle_state: TLifecycleState;
  occurred_at: string;
} {
  const actorUserID = event.actor_user_id?.trim() ?? "";
  const occurredAt = event.occurred_at?.trim() ?? "";
  return (
    (event.conversation_type === "group" || event.conversation_type === "channel") &&
    event.lifecycle_state === lifecycleState &&
    actorUserID !== "" &&
    occurredAt !== ""
  );
}

function applyClosedConversationHint(
  conversations: ConversationSummary[],
  chatID: string,
  event: RealtimeEvent & {
    actor_user_id: string;
    lifecycle_state: "closed";
    occurred_at: string;
  },
): ConversationSummary[] {
  const index = conversations.findIndex((conversation) => conversation.chat_id === chatID);
  if (index === -1) {
    return conversations;
  }

  const conversation = conversations[index];
  if (conversation === undefined) {
    return conversations;
  }

  const next = [...conversations];
  next[index] = {
    ...conversation,
    lifecycle_state: "closed",
    closed_at: event.occurred_at.trim(),
    closed_by_user_id: event.actor_user_id.trim(),
  };
  return next;
}

export function shouldNavigateAwayFromRemovedConversation(pathname: string, removedChatID: string): boolean {
  const normalizedChatID = removedChatID.trim();
  if (normalizedChatID === "") {
    return false;
  }

  const match = /^\/app\/chats\/([^/]+)$/.exec(pathname);
  if (match === null) {
    return false;
  }

  const encodedActiveChatID = match[1] ?? "";
  let activeChatID = encodedActiveChatID;
  try {
    activeChatID = decodeURIComponent(encodedActiveChatID);
  } catch {
    activeChatID = encodedActiveChatID;
  }
  return activeChatID === normalizedChatID;
}

export function applyConversationListRealtimeEvent(
  conversations: ConversationSummary[],
  event: RealtimeEvent,
  actorUserID: string | null,
): ConversationListRealtimeResult {
  const chatID = event.chat_id.trim();
  if (chatID === "") {
    return { conversations, shouldRefresh: false };
  }

  const index = conversations.findIndex((conversation) => conversation.chat_id === chatID);
  if (event.type === "conversation.closed") {
    if (!isValidLifecycleEvent(event, "closed")) {
      return { conversations, shouldRefresh: false };
    }
    return {
      conversations: applyClosedConversationHint(conversations, chatID, event),
      shouldRefresh: true,
    };
  }

  if (event.type === "conversation.removed") {
    if (!isValidLifecycleEvent(event, "removed")) {
      return { conversations, shouldRefresh: false };
    }
    return {
      conversations:
        index === -1 ? conversations : conversations.filter((conversation) => conversation.chat_id !== chatID),
      shouldRefresh: true,
      removedChatID: chatID,
    };
  }

  if (event.type === "message.created") {
    if (index === -1) {
      return { conversations, shouldRefresh: true };
    }

    const eventSequence = normalizeSequence(event.sequence);
    if (eventSequence <= 0) {
      return { conversations, shouldRefresh: false };
    }

    const conversation = conversations[index];
    if (conversation === undefined) {
      return { conversations, shouldRefresh: false };
    }

    const currentReadSequence = normalizeSequence(conversation.read_sequence);
    const senderUserID = event.sender_user_id?.trim() ?? "";
    const normalizedActorUserID = actorUserID?.trim() ?? "";
    const isActorSender = normalizedActorUserID !== "" && senderUserID === normalizedActorUserID;
    const nextReadSequence = isActorSender ? Math.max(currentReadSequence, eventSequence) : currentReadSequence;
    const nextConversation = updateConversationReadState(conversation, eventSequence, nextReadSequence);

    if (
      normalizeSequence(conversation.latest_sequence) === normalizeSequence(nextConversation.latest_sequence) &&
      normalizeSequence(conversation.read_sequence) === normalizeSequence(nextConversation.read_sequence) &&
      normalizeConversationUnreadCount(conversation) === normalizeConversationUnreadCount(nextConversation)
    ) {
      return { conversations, shouldRefresh: false };
    }

    const next = [...conversations];
    next[index] = nextConversation;
    return { conversations: next, shouldRefresh: false };
  }

  if (event.type !== "receipt.updated") {
    return { conversations, shouldRefresh: false };
  }

  const normalizedActorUserID = actorUserID?.trim() ?? "";
  if (normalizedActorUserID === "" || event.receipt_user_id?.trim() !== normalizedActorUserID || index === -1) {
    return { conversations, shouldRefresh: false };
  }

  const readSequence = normalizeSequence(event.read_seq);
  if (readSequence <= 0) {
    return { conversations, shouldRefresh: false };
  }

  const conversation = conversations[index];
  if (conversation === undefined) {
    return { conversations, shouldRefresh: false };
  }

  const latestSequence = Math.max(
    normalizeSequence(conversation.latest_sequence),
    normalizeSequence(event.delivered_seq),
    readSequence,
  );
  const nextConversation = updateConversationReadState(conversation, latestSequence, readSequence);
  const next = [...conversations];
  next[index] = nextConversation;
  return { conversations: next, shouldRefresh: false };
}

function describeConversationPreview(conversation: ConversationSummary): string {
  const collaborativeDescription = conversation.description?.trim() ?? "";
  if ((conversation.type === "group" || conversation.type === "channel") && collaborativeDescription !== "") {
    return collaborativeDescription;
  }

  switch (conversation.type) {
    case "direct":
      return runtimeI18n.t("chatList.preview.direct");
    case "group":
      return runtimeI18n.t("chatList.preview.group");
    case "channel":
      return runtimeI18n.t("chatList.preview.channel");
    case "secret":
      return runtimeI18n.t("chatList.preview.secret");
    default:
      return runtimeI18n.t("chatList.preview.fallback");
  }
}

export function deriveProfileAvatarLabel(profile: SelfProfile | null): string {
  const source = profile?.display_name?.trim() || profile?.nickname?.trim() || "";
  if (source !== "") {
    return deriveConversationInitial(source);
  }
  return runtimeI18n.t("chatList.shell.profileFallback");
}

function isDirectRenderableURL(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:") ||
    normalized.startsWith("data:")
  );
}

interface ChatListPageProps {
  initialConversations?: ConversationSummary[];
  disableAutoLoad?: boolean;
  initialContactsPanelOpen?: boolean;
  initialDiscoveryPanelOpen?: boolean;
}

interface ConversationAvatarPreview {
  source: string;
  label: string;
  chatID: string;
}

export interface ContactDirectChatLaunchDependencies {
  createDirectConversation: (userID: string) => Promise<string>;
  reloadConversations: () => Promise<void>;
  openConversation: (chatID: string) => void;
  closeContactsPanel: () => void;
  handleError?: (error: unknown) => boolean;
}

export async function launchDirectChatFromContact(
  contactUserID: string,
  dependencies: ContactDirectChatLaunchDependencies,
): Promise<string | null> {
  try {
    const chatID = await dependencies.createDirectConversation(contactUserID);
    dependencies.closeContactsPanel();
    await dependencies.reloadConversations();
    dependencies.openConversation(chatID);
    return chatID;
  } catch (error) {
    if (dependencies.handleError?.(error) === true) {
      return null;
    }
    throw error;
  }
}

export interface DiscoveryJoinRefreshDependencies {
  listConversations: () => Promise<ConversationListResponse>;
  setConversations: (conversations: ConversationSummary[]) => void;
  openConversation: (chatID: string) => void;
  closeDiscoveryPanel: () => void;
  handleError?: (error: unknown) => boolean;
}

export async function refreshConversationsAfterDiscoveryJoin(
  chatID: string,
  dependencies: DiscoveryJoinRefreshDependencies,
): Promise<boolean> {
  try {
    const response = await dependencies.listConversations();
    dependencies.setConversations(response.conversations);
    dependencies.closeDiscoveryPanel();
    if (response.conversations.some((conversation) => conversation.chat_id === chatID)) {
      dependencies.openConversation(chatID);
      return true;
    }
    return false;
  } catch (error) {
    if (dependencies.handleError?.(error) === true) {
      return false;
    }
    throw error;
  }
}

export function ChatListPage({
  initialConversations,
  disableAutoLoad = false,
  initialContactsPanelOpen = false,
  initialDiscoveryPanelOpen = false,
}: ChatListPageProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<ConversationSummary[]>(() => initialConversations ?? []);
  const [loading, setLoading] = useState(initialConversations === undefined && !disableAutoLoad);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [chatTypeMenuOpen, setChatTypeMenuOpen] = useState(false);
  const [activeCreateType, setActiveCreateType] = useState<ChatCreateType | null>(null);

  const [creatingDirect, setCreatingDirect] = useState(false);
  const [directPeerUserID, setDirectPeerUserID] = useState("");
  const [directCreateError, setDirectCreateError] = useState<string | null>(null);

  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [groupMemberInput, setGroupMemberInput] = useState("");
  const [groupMembers, setGroupMembers] = useState<string[]>([]);
  const [groupAvatarFile, setGroupAvatarFile] = useState<File | null>(null);
  const [groupInputError, setGroupInputError] = useState<string | null>(null);
  const [groupCreateError, setGroupCreateError] = useState<string | null>(null);

  const [creatingChannel, setCreatingChannel] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [channelDescription, setChannelDescription] = useState("");
  const [channelPublisherInput, setChannelPublisherInput] = useState("");
  const [channelPublishers, setChannelPublishers] = useState<string[]>([]);
  const [channelAvatarFile, setChannelAvatarFile] = useState<File | null>(null);
  const [channelInputError, setChannelInputError] = useState<string | null>(null);
  const [channelCreateError, setChannelCreateError] = useState<string | null>(null);

  const [creatingSecret, setCreatingSecret] = useState(false);
  const [secretPeerIdentifier, setSecretPeerIdentifier] = useState("");
  const [secretCreateError, setSecretCreateError] = useState<string | null>(null);

  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [contactsPanelOpen, setContactsPanelOpen] = useState(initialContactsPanelOpen);
  const [discoveryPanelOpen, setDiscoveryPanelOpen] = useState(initialDiscoveryPanelOpen);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profile, setProfile] = useState<SelfProfile | null>(null);
  const [resolvedProfileAvatarURL, setResolvedProfileAvatarURL] = useState("");
  const [collaborativeAvatarSources, setCollaborativeAvatarSources] = useState<Record<string, string>>({});
  const [directPeerAvatarSources, setDirectPeerAvatarSources] = useState<Record<string, string>>({});
  const [failedDirectAvatarUsers, setFailedDirectAvatarUsers] = useState<Record<string, true>>({});
  const [avatarPreview, setAvatarPreview] = useState<ConversationAvatarPreview | null>(null);
  const [, setDisplayNameRevision] = useState(0);
  const collaborativeAvatarSourcesRef = useRef<Record<string, string>>({});
  const collaborativeAvatarHydrationRef = useRef<Set<string>>(new Set());
  const directPeerAvatarSourcesRef = useRef<Record<string, string>>({});
  const directPeerAvatarHydrationRef = useRef<Set<string>>(new Set());
  const deliveredReceiptClaimsRef = useRef<Set<string>>(new Set());
  const lastAvatarPreviewTriggerRef = useRef<HTMLButtonElement | null>(null);

  const actorUserID = useMemo(() => readActorUserIDFromAccessToken(authStore.getAccessToken()), []);

  const handleUnauthorized = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof GatewayAPIError) || error.code !== "unauthorized") {
        return false;
      }
      authStore.clear();
      navigate("/login", { replace: true });
      return true;
    },
    [navigate],
  );

  const loadConversations = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await listConversations({
        limit: 50,
      });
      setConversations(response.conversations);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.loadConversations");
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized, t]);

  const loadSelfProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const currentProfile = await getSelfProfile();
      setProfile(currentProfile);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.loadProfile");
      setProfileError(message);
    } finally {
      setProfileLoading(false);
    }
  }, [handleUnauthorized, t]);

  const onOpenProfilePanel = () => {
    setContactsPanelOpen(false);
    setDiscoveryPanelOpen(false);
    setProfilePanelOpen(true);
    void loadSelfProfile();
  };

  const onReplaceAvatar = useCallback(
    async (avatar: Blob, filename: string): Promise<void> => {
      try {
        const updated = await replaceProfileAvatar(avatar, filename);
        setProfile(updated);
        setProfileError(null);
      } catch (error) {
        handleUnauthorized(error);
        throw error;
      }
    },
    [handleUnauthorized],
  );

  const onRemoveAvatar = useCallback(async (): Promise<void> => {
    try {
      const updated = await removeProfileAvatar();
      setProfile(updated);
      setProfileError(null);
    } catch (error) {
      handleUnauthorized(error);
      throw error;
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (disableAutoLoad || initialConversations !== undefined) {
      setLoading(false);
      return;
    }

    void loadConversations();
  }, [disableAutoLoad, initialConversations, loadConversations]);

  useEffect(() => {
    const accessToken = authStore.getAccessToken();
    if (accessToken === null || actorUserID === null) {
      return;
    }

    let cancelled = false;
    let refreshInFlight = false;
    const refreshConversations = () => {
      if (refreshInFlight) {
        return;
      }
      refreshInFlight = true;
      void loadConversations().finally(() => {
        refreshInFlight = false;
      });
    };

    const subscription = connectRealtime({
      accessToken,
      onEvent: (event) => {
        if (cancelled) {
          return;
        }
        const deliveredClaim = claimRealtimeDeliveredReceipt(event, actorUserID, deliveredReceiptClaimsRef.current);
        if (deliveredClaim !== null) {
          void markConversationDelivered(deliveredClaim.chatID, deliveredClaim.sequence).catch((error) => {
            handleUnauthorized(error);
          });
        }
        setConversations((current) => {
          const result = applyConversationListRealtimeEvent(current, event, actorUserID);
          if (result.shouldRefresh) {
            refreshConversations();
          }
          if (
            result.removedChatID !== undefined &&
            typeof window !== "undefined" &&
            shouldNavigateAwayFromRemovedConversation(window.location.pathname, result.removedChatID)
          ) {
            navigate("/app", { replace: true });
          }
          return result.conversations;
        });
      },
    });

    return () => {
      cancelled = true;
      subscription.close();
    };
  }, [actorUserID, handleUnauthorized, loadConversations, navigate]);

  useEffect(() => {
    void loadSelfProfile();
  }, [loadSelfProfile]);

  useEffect(() => {
    let cancelled = false;
    let objectURL: string | null = null;

    const rawAvatarURL = profile?.avatar_url?.trim() ?? "";
    if (rawAvatarURL === "") {
      setResolvedProfileAvatarURL("");
      return;
    }
    if (isDirectRenderableURL(rawAvatarURL)) {
      setResolvedProfileAvatarURL(rawAvatarURL);
      return;
    }

    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      setResolvedProfileAvatarURL("");
      return;
    }

    const loadAvatar = async () => {
      try {
        const response = await fetch("/v1/profile/avatar", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          if (!cancelled) {
            setResolvedProfileAvatarURL("");
          }
          return;
        }

        const blob = await response.blob();
        if (blob.size <= 0) {
          if (!cancelled) {
            setResolvedProfileAvatarURL("");
          }
          return;
        }

        objectURL = URL.createObjectURL(blob);
        if (!cancelled) {
          setResolvedProfileAvatarURL(objectURL);
        }
      } catch {
        if (!cancelled) {
          setResolvedProfileAvatarURL("");
        }
      }
    };

    void loadAvatar();

    return () => {
      cancelled = true;
      if (objectURL !== null) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [profile?.avatar_url]);

  useEffect(() => {
    if (conversations.length === 0) {
      return;
    }

    let cancelled = false;

    const hydrateDisplayNames = async () => {
      const directPeerUserIDs = collectDirectPeerUserIDs(conversations, actorUserID);
      const changed = await resolveUserDisplayNames(directPeerUserIDs);
      if (!cancelled && changed) {
        setDisplayNameRevision((value) => value + 1);
      }
      if (cancelled) {
        return;
      }

      for (const conversation of conversations) {
        if (conversation.type !== "direct") {
          continue;
        }
        const descriptor = describeDirectPeerAvatar(conversation, actorUserID);
        if (descriptor.peerUserID === null || descriptor.avatarBinaryPath === null) {
          continue;
        }
        if (directPeerAvatarSourcesRef.current[descriptor.peerUserID] !== undefined) {
          continue;
        }
        if (directPeerAvatarHydrationRef.current.has(descriptor.peerUserID)) {
          continue;
        }
        const accessToken = authStore.getAccessToken();
        if (accessToken === null) {
          continue;
        }

        const userID = descriptor.peerUserID;
        directPeerAvatarHydrationRef.current.add(userID);
        void (async () => {
          try {
            const response = await fetch(descriptor.avatarBinaryPath ?? "", {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            if (!response.ok) {
              return;
            }
            const binary = await response.blob();
            if (binary.size <= 0) {
              return;
            }
            const sourceURL = URL.createObjectURL(binary);
            setDirectPeerAvatarSources((current) => {
              const previous = current[userID];
              if (previous === sourceURL) {
                return current;
              }
              if (previous !== undefined) {
                URL.revokeObjectURL(previous);
              }
              return {
                ...current,
                [userID]: sourceURL,
              };
            });
          } catch {
            return;
          } finally {
            directPeerAvatarHydrationRef.current.delete(userID);
          }
        })();
      }
    };

    void hydrateDisplayNames();

    return () => {
      cancelled = true;
    };
  }, [actorUserID, conversations]);

  useEffect(() => {
    const accessToken = authStore.getAccessToken();
    if (accessToken === null) {
      return;
    }

    for (const conversation of conversations) {
      if (conversation.type !== "group" && conversation.type !== "channel") {
        continue;
      }
      const avatarAssetID = normalizeConversationAvatarAssetID(conversation.avatar_asset_id);
      if (avatarAssetID === "") {
        continue;
      }
      const avatarSourceKey = resolveConversationAvatarSourceKey(conversation.chat_id, avatarAssetID);
      if (collaborativeAvatarSourcesRef.current[avatarSourceKey] !== undefined) {
        continue;
      }
      if (collaborativeAvatarHydrationRef.current.has(avatarSourceKey)) {
        continue;
      }

      const path = resolveConversationAvatarAssetPath(avatarAssetID, conversation.chat_id);
      if (path === null) {
        continue;
      }

      collaborativeAvatarHydrationRef.current.add(avatarSourceKey);
      void (async () => {
        try {
          const response = await fetch(path, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          if (!response.ok) {
            return;
          }
          const binary = await response.blob();
          if (binary.size <= 0) {
            return;
          }
          const sourceURL = URL.createObjectURL(binary);
          setCollaborativeAvatarSources((current) => {
            const previous = current[avatarSourceKey];
            if (previous === sourceURL) {
              return current;
            }
            if (previous !== undefined) {
              URL.revokeObjectURL(previous);
            }
            return {
              ...current,
              [avatarSourceKey]: sourceURL,
            };
          });
        } catch {
          return;
        } finally {
          collaborativeAvatarHydrationRef.current.delete(avatarSourceKey);
        }
      })();
    }
  }, [conversations]);

  useEffect(() => {
    collaborativeAvatarSourcesRef.current = collaborativeAvatarSources;
  }, [collaborativeAvatarSources]);

  useEffect(() => {
    directPeerAvatarSourcesRef.current = directPeerAvatarSources;
  }, [directPeerAvatarSources]);

  useEffect(() => {
    return () => {
      for (const sourceURL of Object.values(collaborativeAvatarSourcesRef.current)) {
        URL.revokeObjectURL(sourceURL);
      }
      collaborativeAvatarHydrationRef.current.clear();
      for (const sourceURL of Object.values(directPeerAvatarSourcesRef.current)) {
        URL.revokeObjectURL(sourceURL);
      }
      directPeerAvatarHydrationRef.current.clear();
    };
  }, []);

  const openConversation = (chatID: string) => {
    navigate(`/app/chats/${encodeURIComponent(chatID)}`);
  };

  const focusLastAvatarPreviewTrigger = useCallback(() => {
    const trigger = lastAvatarPreviewTriggerRef.current;
    if (trigger === null) {
      return;
    }
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => trigger.focus());
      return;
    }
    trigger.focus();
  }, []);

  const closeAvatarPreview = useCallback(() => {
    setAvatarPreview(null);
    focusLastAvatarPreviewTrigger();
  }, [focusLastAvatarPreviewTrigger]);

  const openAvatarPreview = useCallback((preview: ConversationAvatarPreview, trigger: HTMLButtonElement) => {
    lastAvatarPreviewTriggerRef.current = trigger;
    setAvatarPreview(preview);
  }, []);

  useEffect(() => {
    if (avatarPreview === null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeAvatarPreview();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [avatarPreview, closeAvatarPreview]);

  const onSignOut = () => {
    authStore.clear();
    navigate("/login", { replace: true });
  };

  const closeCreatePanel = () => {
    setActiveCreateType(null);
    setDirectCreateError(null);
    setGroupCreateError(null);
    setGroupInputError(null);
    setChannelCreateError(null);
    setChannelInputError(null);
    setGroupAvatarFile(null);
    setChannelAvatarFile(null);
    setSecretCreateError(null);
  };

  const openCreatePanel = (type: ChatCreateType) => {
    setChatTypeMenuOpen(false);
    setContactsPanelOpen(false);
    setDiscoveryPanelOpen(false);
    setActiveCreateType(type);
    setDirectCreateError(null);
    setGroupCreateError(null);
    setGroupInputError(null);
    setChannelCreateError(null);
    setChannelInputError(null);
    setSecretCreateError(null);
  };

  const onToggleTypeMenu = () => {
    if (chatTypeMenuOpen) {
      setChatTypeMenuOpen(false);
      return;
    }
    setContactsPanelOpen(false);
    setDiscoveryPanelOpen(false);
    closeCreatePanel();
    setChatTypeMenuOpen(true);
  };

  const onToggleContactsPanel = () => {
    if (contactsPanelOpen) {
      setContactsPanelOpen(false);
      return;
    }
    closeCreatePanel();
    setChatTypeMenuOpen(false);
    setProfilePanelOpen(false);
    setDiscoveryPanelOpen(false);
    setContactsPanelOpen(true);
  };

  const onToggleDiscoveryPanel = () => {
    if (discoveryPanelOpen) {
      setDiscoveryPanelOpen(false);
      return;
    }
    closeCreatePanel();
    setChatTypeMenuOpen(false);
    setProfilePanelOpen(false);
    setContactsPanelOpen(false);
    setDiscoveryPanelOpen(true);
  };

  const onStartContactChat = useCallback(
    async (contactUserID: string): Promise<void> => {
      await launchDirectChatFromContact(contactUserID, {
        createDirectConversation,
        reloadConversations: loadConversations,
        openConversation: (chatID) => navigate(`/app/chats/${encodeURIComponent(chatID)}`),
        closeContactsPanel: () => setContactsPanelOpen(false),
        handleError: handleUnauthorized,
      });
    },
    [handleUnauthorized, loadConversations, navigate],
  );

  const onStartDiscoveryDirectChat = useCallback(
    async (userID: string): Promise<void> => {
      await launchDirectChatFromContact(userID, {
        createDirectConversation,
        reloadConversations: loadConversations,
        openConversation: (chatID) => navigate(`/app/chats/${encodeURIComponent(chatID)}`),
        closeContactsPanel: () => setDiscoveryPanelOpen(false),
        handleError: handleUnauthorized,
      });
    },
    [handleUnauthorized, loadConversations, navigate],
  );

  const onDiscoveryConversationJoined = useCallback(
    async (chatID: string): Promise<void> => {
      await refreshConversationsAfterDiscoveryJoin(chatID, {
        listConversations: () => listConversations({ limit: 50 }),
        setConversations,
        openConversation: (joinedChatID) => navigate(`/app/chats/${encodeURIComponent(joinedChatID)}`),
        closeDiscoveryPanel: () => setDiscoveryPanelOpen(false),
        handleError: handleUnauthorized,
      });
    },
    [handleUnauthorized, navigate],
  );

  const onCreateDirectConversation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedPeer = directPeerUserID.trim();
    if (normalizedPeer === "") {
      setDirectCreateError(t("chatList.errors.directPeerIdentifierRequired"));
      return;
    }

    setCreatingDirect(true);
    setDirectCreateError(null);

    try {
      const peerUserID = looksLikeUserID(normalizedPeer)
        ? normalizedPeer
        : await resolveUserIDByIdentifier(normalizedPeer);
      const chatID = await createDirectConversation(peerUserID);
      setDirectPeerUserID("");
      closeCreatePanel();
      await loadConversations();
      navigate(`/app/chats/${encodeURIComponent(chatID)}`);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (error instanceof GatewayAPIError) {
        switch (error.code) {
          case "not_found":
            setDirectCreateError(t("chatList.errors.secretUserNotFound"));
            return;
          case "invalid_request":
            setDirectCreateError(t("chatList.errors.secretInvalidIdentifier"));
            return;
          default:
            break;
        }
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.directCreateFallback");
      setDirectCreateError(message);
    } finally {
      setCreatingDirect(false);
    }
  };

  const addGroupMember = () => {
    const normalized = groupMemberInput.trim();
    if (normalized === "") {
      setGroupInputError(t("chatList.errors.groupMemberIdentifierRequired"));
      return;
    }

    setGroupMembers((current) => {
      if (current.includes(normalized)) {
        return current;
      }
      return [...current, normalized];
    });
    setGroupMemberInput("");
    setGroupInputError(null);
  };

  const removeGroupMember = (identifier: string) => {
    setGroupMembers((current) => current.filter((entry) => entry !== identifier));
  };

  const addChannelPublisher = () => {
    const normalized = channelPublisherInput.trim();
    if (normalized === "") {
      setChannelInputError(t("chatList.errors.channelPublisherIdentifierRequired"));
      return;
    }

    setChannelPublishers((current) => {
      if (current.includes(normalized)) {
        return current;
      }
      return [...current, normalized];
    });
    setChannelPublisherInput("");
    setChannelInputError(null);
  };

  const removeChannelPublisher = (identifier: string) => {
    setChannelPublishers((current) => current.filter((entry) => entry !== identifier));
  };

  const onCreateGroup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const groupNameError = validateCollaborativeName(groupName);
    if (groupNameError !== null) {
      setGroupCreateError(groupNameError);
      return;
    }
    const groupDescriptionError = validateCollaborativeDescription(groupDescription);
    if (groupDescriptionError !== null) {
      setGroupCreateError(groupDescriptionError);
      return;
    }
    if (normalizeIdentifierList(groupMembers).length === 0) {
      setGroupCreateError(t("chatList.errors.groupAtLeastOneMember"));
      return;
    }

    setCreatingGroup(true);
    setGroupCreateError(null);

    try {
      let avatarAssetID: string | undefined;
      if (groupAvatarFile !== null) {
        try {
          avatarAssetID = await uploadAvatarMedia(groupAvatarFile);
        } catch (error) {
          if (handleUnauthorized(error)) {
            return;
          }
          setGroupCreateError(mapCollaborativeAvatarUploadErrorMessage(error));
          return;
        }
      }

      const chatID = await createGroupConversation({
        name: groupName,
        description: groupDescription,
        participantIdentifiers: groupMembers,
        avatarAssetID,
      });
      setGroupName("");
      setGroupDescription("");
      setGroupMemberInput("");
      setGroupMembers([]);
      setGroupAvatarFile(null);
      closeCreatePanel();
      await loadConversations();
      navigate(`/app/chats/${encodeURIComponent(chatID)}`);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (error instanceof GatewayAPIError) {
        setGroupCreateError(collaborativeCreateErrorMessage(error, "group"));
        return;
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.groupCreateFallback");
      setGroupCreateError(message);
    } finally {
      setCreatingGroup(false);
    }
  };

  const onCreateChannel = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const channelNameError = validateCollaborativeName(channelName);
    if (channelNameError !== null) {
      setChannelCreateError(channelNameError);
      return;
    }
    const channelDescriptionError = validateCollaborativeDescription(channelDescription);
    if (channelDescriptionError !== null) {
      setChannelCreateError(channelDescriptionError);
      return;
    }
    if (normalizeIdentifierList(channelPublishers).length === 0) {
      setChannelCreateError(t("chatList.errors.channelAtLeastOnePublisher"));
      return;
    }

    setCreatingChannel(true);
    setChannelCreateError(null);

    try {
      let avatarAssetID: string | undefined;
      if (channelAvatarFile !== null) {
        try {
          avatarAssetID = await uploadAvatarMedia(channelAvatarFile);
        } catch (error) {
          if (handleUnauthorized(error)) {
            return;
          }
          setChannelCreateError(mapCollaborativeAvatarUploadErrorMessage(error));
          return;
        }
      }

      const chatID = await createChannelConversation({
        name: channelName,
        description: channelDescription,
        participantIdentifiers: channelPublishers,
        avatarAssetID,
      });
      setChannelName("");
      setChannelDescription("");
      setChannelPublisherInput("");
      setChannelPublishers([]);
      setChannelAvatarFile(null);
      closeCreatePanel();
      await loadConversations();
      navigate(`/app/chats/${encodeURIComponent(chatID)}`);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (error instanceof GatewayAPIError) {
        setChannelCreateError(collaborativeCreateErrorMessage(error, "channel"));
        return;
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.channelCreateFallback");
      setChannelCreateError(message);
    } finally {
      setCreatingChannel(false);
    }
  };

  const onCreateSecretConversation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedPeer = secretPeerIdentifier.trim();
    if (!canSubmitSecretCreate(normalizedPeer)) {
      setSecretCreateError(t("chatList.errors.peerIdentifierRequired"));
      return;
    }

    setCreatingSecret(true);
    setSecretCreateError(null);

    try {
      const peerUserID = looksLikeUserID(normalizedPeer)
        ? normalizedPeer
        : await resolveUserIDByIdentifier(normalizedPeer);
      const chatID = await createSecretConversation(peerUserID);
      setSecretPeerIdentifier("");
      closeCreatePanel();
      await loadConversations();
      navigate(`/app/chats/${encodeURIComponent(chatID)}`);
    } catch (error) {
      if (handleUnauthorized(error)) {
        return;
      }
      if (error instanceof GatewayAPIError) {
        setSecretCreateError(mapSecretCreateErrorMessage(error));
        return;
      }
      const message = error instanceof Error ? error.message : t("chatList.errors.secretCreateFallback");
      setSecretCreateError(message);
    } finally {
      setCreatingSecret(false);
    }
  };

  const groupNameValidationError = validateCollaborativeName(groupName);
  const groupDescriptionValidationError = validateCollaborativeDescription(groupDescription);
  const groupHasMembers = normalizeIdentifierList(groupMembers).length > 0;
  const groupReady = canSubmitCollaborativeCreate(groupName, groupDescription, groupMembers);
  const groupValidationMessage =
    groupNameValidationError ??
    groupDescriptionValidationError ??
    (groupHasMembers ? t("chatList.validation.groupReady") : t("chatList.validation.groupRequirements"));

  const channelNameValidationError = validateCollaborativeName(channelName);
  const channelDescriptionValidationError = validateCollaborativeDescription(channelDescription);
  const channelHasPublishers = normalizeIdentifierList(channelPublishers).length > 0;
  const channelReady = canSubmitCollaborativeCreate(channelName, channelDescription, channelPublishers);
  const channelValidationMessage =
    channelNameValidationError ??
    channelDescriptionValidationError ??
    (channelHasPublishers ? t("chatList.validation.channelReady") : t("chatList.validation.channelRequirements"));

  const profileAvatarURL = resolvedProfileAvatarURL.trim();
  const profileWithResolvedAvatar =
    profile === null
      ? null
      : {
          ...profile,
          avatar_url: profileAvatarURL,
        };

  return (
    <main className={CHAT_LIST_SHELL_CLASSES.root}>
      <section className={CHAT_LIST_SHELL_CLASSES.rail} data-testid="conversation-rail">
        <header className="chat-shell-head">
          <div className="chat-shell-heading">
            <p className="chat-shell-eyebrow">{t("chatList.shell.workspaceEyebrow")}</p>
            <h1 className="chat-shell-title">{t("chatList.shell.title")}</h1>
          </div>
          <div className="chat-head-actions">
            <button
              className={`icon-button icon-button-ghost${discoveryPanelOpen ? " icon-button-active" : ""}`}
              type="button"
              data-testid="open-discovery-panel"
              aria-label={discoveryPanelOpen ? "Close discovery" : "Open discovery"}
              title={discoveryPanelOpen ? "Close discovery" : "Open discovery"}
              aria-expanded={discoveryPanelOpen}
              onClick={onToggleDiscoveryPanel}
            >
              <IconSearch />
            </button>
            <button
              className={`icon-button icon-button-ghost${contactsPanelOpen ? " icon-button-active" : ""}`}
              type="button"
              data-testid="open-contacts-panel"
              aria-label={contactsPanelOpen ? t("contacts.entry.close") : t("contacts.entry.open")}
              title={contactsPanelOpen ? t("contacts.entry.close") : t("contacts.entry.open")}
              aria-expanded={contactsPanelOpen}
              onClick={onToggleContactsPanel}
            >
              <IconUsers />
            </button>
            <button
              className="profile-avatar-trigger"
              type="button"
              data-testid="open-profile-avatar-panel"
              aria-label={t("chatList.shell.openProfilePanel")}
              title={t("chatList.shell.openProfilePanel")}
              onClick={onOpenProfilePanel}
            >
              {profileAvatarURL ? (
                <img src={profileAvatarURL} alt="" />
              ) : (
                <span>{deriveProfileAvatarLabel(profileWithResolvedAvatar)}</span>
              )}
            </button>
            <button
              className="icon-button icon-button-ghost"
              type="button"
              onClick={onSignOut}
              aria-label={t("chatList.shell.signOut")}
              title={t("chatList.shell.signOut")}
            >
              <IconLogout />
            </button>
          </div>
        </header>

        <ProfileAvatarPanel
          open={profilePanelOpen}
          loading={profileLoading}
          profile={profileWithResolvedAvatar}
          errorMessage={profileError}
          onClose={() => setProfilePanelOpen(false)}
          onReload={loadSelfProfile}
          onReplaceAvatar={onReplaceAvatar}
          onRemoveAvatar={onRemoveAvatar}
          onProfileUpdated={setProfile}
        />

        {contactsPanelOpen ? (
          <ContactsPanel
            onClose={() => setContactsPanelOpen(false)}
            onStartChat={onStartContactChat}
            onAuthRequired={() => {
              authStore.clear();
              navigate("/login", { replace: true });
            }}
          />
        ) : null}

        {discoveryPanelOpen ? (
          <DiscoveryPanel
            onClose={() => setDiscoveryPanelOpen(false)}
            onStartDirectChat={onStartDiscoveryDirectChat}
            onConversationJoined={onDiscoveryConversationJoined}
            onAuthRequired={() => {
              authStore.clear();
              navigate("/login", { replace: true });
            }}
          />
        ) : null}

        <section className={CHAT_LIST_SHELL_CLASSES.composerLane}>
          <div className="composer-toggle-row">
            <h2 className="lane-title">{t("chatList.shell.newChat")}</h2>
            <button
              className={`icon-button icon-button-primary${chatTypeMenuOpen ? " icon-button-active" : ""}`}
              type="button"
              aria-label={chatTypeMenuOpen ? t("chatList.shell.closeTypeMenu") : t("chatList.shell.openTypeMenu")}
              title={chatTypeMenuOpen ? t("chatList.shell.closeTypeMenu") : t("chatList.shell.openTypeMenu")}
              aria-expanded={chatTypeMenuOpen}
              onClick={onToggleTypeMenu}
            >
              <IconPlus />
            </button>
          </div>

          <div
            className={`${CHAT_LIST_SHELL_CLASSES.startTypeMenu}${chatTypeMenuOpen ? " start-chat-type-menu-open" : ""}`}
            data-testid="chat-type-menu"
            aria-hidden={!chatTypeMenuOpen}
            hidden={!chatTypeMenuOpen}
          >
            {CHAT_TYPE_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.key}
                  className="chat-type-option"
                  type="button"
                  data-testid={`chat-type-${option.key}`}
                  onClick={() => openCreatePanel(option.key)}
                >
                  <span className="chat-type-option-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="chat-type-option-text">
                    <span className="chat-type-option-label">{t(option.labelKey)}</span>
                    <span className="chat-type-option-note">{t(option.noteKey)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {activeCreateType === "direct" ? (
            <form className="stack-form create-panel" onSubmit={onCreateDirectConversation}>
              <div className="create-panel-head">
                <h3 className="chat-section-title">{t("chatList.direct.title")}</h3>
                <button
                  className="icon-button icon-button-ghost"
                  type="button"
                  onClick={closeCreatePanel}
                  aria-label={t("chatList.direct.closePanel")}
                  title={t("chatList.direct.closePanel")}
                >
                  <IconClose />
                </button>
              </div>
              <label className="chat-field">
                <span>{t("chatList.direct.peerLabel")}</span>
                <input
                  data-testid="direct-peer-user-id"
                  value={directPeerUserID}
                  onChange={(event) => setDirectPeerUserID(event.target.value)}
                  placeholder={t("chatList.direct.peerPlaceholder")}
                  disabled={creatingDirect}
                />
              </label>
              {directCreateError !== null ? <p className="inline-error">{directCreateError}</p> : null}
              <button
                className="icon-button icon-button-primary"
                data-testid="create-direct-chat"
                type="submit"
                disabled={creatingDirect || directPeerUserID.trim() === ""}
                aria-label={creatingDirect ? t("chatList.direct.creatingAria") : t("chatList.direct.startAria")}
                title={creatingDirect ? t("chatList.direct.creatingAria") : t("chatList.direct.startAria")}
              >
                <IconSend />
              </button>
            </form>
          ) : null}

          {activeCreateType === "group" ? (
            <form className="stack-form create-panel" onSubmit={onCreateGroup}>
              <div className="create-panel-head">
                <h3 className="chat-section-title">{t("chatList.group.title")}</h3>
                <button
                  className="icon-button icon-button-ghost"
                  type="button"
                  onClick={closeCreatePanel}
                  aria-label={t("chatList.group.closePanel")}
                  title={t("chatList.group.closePanel")}
                >
                  <IconClose />
                </button>
              </div>

              <label className="chat-field">
                <span>{t("chatList.group.nameLabel")}</span>
                <input
                  value={groupName}
                  onChange={(event) => setGroupName(event.target.value)}
                  placeholder={t("chatList.group.namePlaceholder")}
                  disabled={creatingGroup}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatList.group.descriptionLabel")}</span>
                <textarea
                  value={groupDescription}
                  onChange={(event) => setGroupDescription(event.target.value)}
                  placeholder={t("chatList.group.descriptionPlaceholder")}
                  rows={2}
                  disabled={creatingGroup}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatList.group.avatarLabel")}</span>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="group-avatar-input"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0] ?? null;
                    setGroupAvatarFile(nextFile);
                  }}
                  disabled={creatingGroup}
                />
              </label>
              {groupAvatarFile !== null ? (
                <p className="muted-line">{t("chatList.group.avatarSelected", { fileName: groupAvatarFile.name })}</p>
              ) : null}
              <label className="chat-field">
                <span>{t("chatList.group.addMemberLabel")}</span>
                <div className="identifier-editor-row">
                  <input
                    value={groupMemberInput}
                    onChange={(event) => setGroupMemberInput(event.target.value)}
                    placeholder={t("chatList.group.memberPlaceholder")}
                    disabled={creatingGroup}
                  />
                  <button className="secondary-button" type="button" onClick={addGroupMember} disabled={creatingGroup}>
                    {t("chatList.shared.add")}
                  </button>
                </div>
              </label>
              {groupInputError !== null ? <p className="inline-error">{groupInputError}</p> : null}
              {groupMembers.length > 0 ? (
                <ul className="identifier-chip-list">
                  {groupMembers.map((identifier) => (
                    <li key={identifier}>
                      <span className="identifier-chip">{identifier}</span>
                      <button
                        className="identifier-chip-remove"
                        type="button"
                        onClick={() => removeGroupMember(identifier)}
                        aria-label={t("chatList.shared.removeWithIdentifier", { identifier })}
                        disabled={creatingGroup}
                      >
                        {t("chatList.shared.remove")}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p
                className={`create-panel-validation${groupReady ? " create-panel-validation-ready" : groupNameValidationError !== null || groupDescriptionValidationError !== null ? " create-panel-validation-error" : ""}`}
                data-testid="group-create-validation"
              >
                {groupValidationMessage}
              </p>
              {groupCreateError !== null ? <p className="inline-error">{groupCreateError}</p> : null}
              <button className="primary-button" type="submit" disabled={creatingGroup || !groupReady}>
                {creatingGroup ? t("chatList.group.creating") : t("chatList.group.create")}
              </button>
            </form>
          ) : null}

          {activeCreateType === "channel" ? (
            <form className="stack-form create-panel" onSubmit={onCreateChannel}>
              <div className="create-panel-head">
                <h3 className="chat-section-title">{t("chatList.channel.title")}</h3>
                <button
                  className="icon-button icon-button-ghost"
                  type="button"
                  onClick={closeCreatePanel}
                  aria-label={t("chatList.channel.closePanel")}
                  title={t("chatList.channel.closePanel")}
                >
                  <IconClose />
                </button>
              </div>

              <label className="chat-field">
                <span>{t("chatList.channel.nameLabel")}</span>
                <input
                  value={channelName}
                  onChange={(event) => setChannelName(event.target.value)}
                  placeholder={t("chatList.channel.namePlaceholder")}
                  disabled={creatingChannel}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatList.channel.descriptionLabel")}</span>
                <textarea
                  value={channelDescription}
                  onChange={(event) => setChannelDescription(event.target.value)}
                  placeholder={t("chatList.channel.descriptionPlaceholder")}
                  rows={2}
                  disabled={creatingChannel}
                />
              </label>
              <label className="chat-field">
                <span>{t("chatList.channel.avatarLabel")}</span>
                <input
                  type="file"
                  accept="image/*"
                  data-testid="channel-avatar-input"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0] ?? null;
                    setChannelAvatarFile(nextFile);
                  }}
                  disabled={creatingChannel}
                />
              </label>
              {channelAvatarFile !== null ? (
                <p className="muted-line">{t("chatList.channel.avatarSelected", { fileName: channelAvatarFile.name })}</p>
              ) : null}
              <label className="chat-field">
                <span>{t("chatList.channel.addPublisherLabel")}</span>
                <div className="identifier-editor-row">
                  <input
                    value={channelPublisherInput}
                    onChange={(event) => setChannelPublisherInput(event.target.value)}
                    placeholder={t("chatList.channel.publisherPlaceholder")}
                    disabled={creatingChannel}
                  />
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={addChannelPublisher}
                    disabled={creatingChannel}
                  >
                    {t("chatList.shared.add")}
                  </button>
                </div>
              </label>
              {channelInputError !== null ? <p className="inline-error">{channelInputError}</p> : null}
              {channelPublishers.length > 0 ? (
                <ul className="identifier-chip-list">
                  {channelPublishers.map((identifier) => (
                    <li key={identifier}>
                      <span className="identifier-chip">{identifier}</span>
                      <button
                        className="identifier-chip-remove"
                        type="button"
                        onClick={() => removeChannelPublisher(identifier)}
                        aria-label={t("chatList.shared.removeWithIdentifier", { identifier })}
                        disabled={creatingChannel}
                      >
                        {t("chatList.shared.remove")}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p
                className={`create-panel-validation${channelReady ? " create-panel-validation-ready" : channelNameValidationError !== null || channelDescriptionValidationError !== null ? " create-panel-validation-error" : ""}`}
                data-testid="channel-create-validation"
              >
                {channelValidationMessage}
              </p>
              {channelCreateError !== null ? <p className="inline-error">{channelCreateError}</p> : null}
              <button className="primary-button" type="submit" disabled={creatingChannel || !channelReady}>
                {creatingChannel ? t("chatList.channel.creating") : t("chatList.channel.create")}
              </button>
            </form>
          ) : null}

          {activeCreateType === "secret" ? (
            <form className="stack-form create-panel create-panel-secret" onSubmit={onCreateSecretConversation}>
              <div className="create-panel-head">
                <h3 className="chat-section-title">{t("chatList.secret.title")}</h3>
                <button
                  className="icon-button icon-button-ghost"
                  type="button"
                  onClick={closeCreatePanel}
                  aria-label={t("chatList.secret.closePanel")}
                  title={t("chatList.secret.closePanel")}
                >
                  <IconClose />
                </button>
              </div>
              <label className="chat-field">
                <span>{t("chatList.secret.peerLabel")}</span>
                <input
                  data-testid="secret-peer-identifier"
                  value={secretPeerIdentifier}
                  onChange={(event) => setSecretPeerIdentifier(event.target.value)}
                  placeholder={t("chatList.secret.peerPlaceholder")}
                  disabled={creatingSecret}
                />
              </label>
              {secretCreateError !== null ? <p className="inline-error">{secretCreateError}</p> : null}
              <button
                className="icon-button icon-button-primary"
                data-testid="create-secret-chat"
                type="submit"
                disabled={creatingSecret || !canSubmitSecretCreate(secretPeerIdentifier)}
                aria-label={creatingSecret ? t("chatList.secret.creatingAria") : t("chatList.secret.startAria")}
                title={creatingSecret ? t("chatList.secret.creatingAria") : t("chatList.secret.startAria")}
              >
                <IconSend />
              </button>
            </form>
          ) : null}
        </section>

        <section className="conversation-list-shell">
          <div className="section-row">
            <h2 className="lane-title">{t("chatList.shell.recentConversations")}</h2>
            <button
              className="icon-button icon-button-ghost"
              type="button"
              aria-label={t("chatList.shell.refreshConversations")}
              title={t("chatList.shell.refreshConversations")}
              onClick={() => void loadConversations()}
              disabled={loading}
            >
              <IconRefresh />
            </button>
          </div>

          {loading ? <p className="muted-line">{t("chatList.shell.loading")}</p> : null}
          {errorMessage !== null ? (
            <p className="inline-error" data-testid="conversation-load-error">
              {errorMessage}
            </p>
          ) : null}

          {!loading && conversations.length === 0 ? (
            <p className="muted-line">{t("chatList.shell.empty")}</p>
          ) : null}

          <ul className={CHAT_LIST_SHELL_CLASSES.list}>
            {conversations.map((conversation) => {
              const rowText = resolveConversationRowText(conversation, actorUserID);
              const timestampText = formatConversationTimestamp(resolveConversationTimestampSource(conversation));
              const unreadCount = normalizeConversationUnreadCount(conversation);
              const isSecretConversation = conversation.type === "secret";
              const directAvatarDescriptor =
                conversation.type === "direct" ? describeDirectPeerAvatar(conversation, actorUserID) : null;
              const avatarInitial =
                directAvatarDescriptor === null
                  ? deriveConversationInitial(rowText.primary)
                  : deriveConversationInitial(directAvatarDescriptor.label);
              const avatarSource =
                directAvatarDescriptor === null ||
                directAvatarDescriptor.peerUserID === null ||
                failedDirectAvatarUsers[directAvatarDescriptor.peerUserID] === true
                  ? ""
                  : isAvatarReferenceDirectlyRenderable(directAvatarDescriptor.avatarReference)
                    ? directAvatarDescriptor.avatarReference
                    : directPeerAvatarSources[directAvatarDescriptor.peerUserID] ?? "";
              const collaborativeAvatarAssetID =
                conversation.type === "group" || conversation.type === "channel"
                  ? normalizeConversationAvatarAssetID(conversation.avatar_asset_id)
                  : "";
              const collaborativeAvatarSource =
                collaborativeAvatarAssetID === ""
                  ? ""
                  : collaborativeAvatarSources[
                      resolveConversationAvatarSourceKey(conversation.chat_id, collaborativeAvatarAssetID)
                    ] ?? "";
              const resolvedAvatarSource = conversation.type === "direct" ? avatarSource : collaborativeAvatarSource;
              const avatarPreviewLabel =
                directAvatarDescriptor === null
                  ? describeConversationWithDisplayName(conversation, actorUserID)
                  : directAvatarDescriptor.label;
              const rowAriaLabel =
                unreadCount > 0 ? t("chatList.unread.rowLabel", { title: rowText.primary, count: unreadCount }) : rowText.primary;
              const onAvatarImageError = () => {
                if (conversation.type !== "direct") {
                  if (collaborativeAvatarAssetID === "") {
                    return;
                  }
                  const collaborativeAvatarSourceKey = resolveConversationAvatarSourceKey(
                    conversation.chat_id,
                    collaborativeAvatarAssetID,
                  );
                  setCollaborativeAvatarSources((current) => {
                    const existing = current[collaborativeAvatarSourceKey];
                    if (existing === undefined) {
                      return current;
                    }
                    if (existing.startsWith("blob:")) {
                      URL.revokeObjectURL(existing);
                    }
                    const next = { ...current };
                    delete next[collaborativeAvatarSourceKey];
                    return next;
                  });
                  return;
                }
                const peerUserID = directAvatarDescriptor?.peerUserID ?? null;
                if (peerUserID === null) {
                  return;
                }
                setFailedDirectAvatarUsers((current) => {
                  if (current[peerUserID] === true) {
                    return current;
                  }
                  return {
                    ...current,
                    [peerUserID]: true,
                  };
                });
              };
              const avatarNode = (
                <span className="conversation-avatar" aria-hidden="true">
                  {resolvedAvatarSource !== "" ? (
                    <img className="conversation-avatar-image" src={resolvedAvatarSource} alt="" onError={onAvatarImageError} />
                  ) : (
                    avatarInitial
                  )}
                  {unreadCount > 0 ? (
                    <span
                      className="conversation-unread-badge"
                      data-testid={`conversation-unread-badge-${conversation.chat_id}`}
                    >
                      {formatConversationUnreadBadge(unreadCount)}
                    </span>
                  ) : null}
                </span>
              );
              return (
                <li key={conversation.chat_id} className="conversation-list-row">
                  {resolvedAvatarSource !== "" ? (
                    <button
                      className="conversation-avatar-button"
                      data-testid={`conversation-avatar-button-${conversation.chat_id}`}
                      type="button"
                      aria-label={t("chatList.avatarPreview.open", { label: avatarPreviewLabel })}
                      title={t("chatList.avatarPreview.open", { label: avatarPreviewLabel })}
                      onClick={(event) =>
                        openAvatarPreview(
                          {
                            source: resolvedAvatarSource,
                            label: avatarPreviewLabel,
                            chatID: conversation.chat_id,
                          },
                          event.currentTarget,
                        )
                      }
                    >
                      {avatarNode}
                    </button>
                  ) : (
                    <span className="conversation-avatar-shell">{avatarNode}</span>
                  )}
                  <button
                    className={CHAT_LIST_SHELL_CLASSES.item}
                    data-testid={`conversation-${conversation.chat_id}`}
                    type="button"
                    aria-label={rowAriaLabel}
                    onClick={() => openConversation(conversation.chat_id)}
                  >
                    <span className="conversation-content">
                      <span className="conversation-topline">
                        <span className="conversation-title">{rowText.primary}</span>
                        {isSecretConversation ? (
                          <span className="conversation-type-badge conversation-type-badge-secret">
                            <IconLock />
                            <span className="conversation-type-badge-label">{t("chatList.secret.badge")}</span>
                          </span>
                        ) : null}
                        <span className="conversation-meta">{timestampText}</span>
                      </span>
                      <span className="conversation-preview">{rowText.secondary}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </section>
      {avatarPreview !== null ? (
        <div
          className="avatar-preview-dialog"
          data-testid="avatar-preview-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={t("chatList.avatarPreview.dialog", { label: avatarPreview.label })}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeAvatarPreview();
            }
          }}
        >
          <div className="avatar-preview-content">
            <img
              className="avatar-preview-image"
              data-testid="avatar-preview-image"
              src={avatarPreview.source}
              alt={t("chatList.avatarPreview.image", { label: avatarPreview.label })}
            />
            <button
              className="icon-button icon-button-ghost avatar-preview-close"
              data-testid="avatar-preview-close"
              type="button"
              aria-label={t("chatList.avatarPreview.close", { label: avatarPreview.label })}
              title={t("chatList.avatarPreview.close", { label: avatarPreview.label })}
              onClick={closeAvatarPreview}
            >
              <IconClose />
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}
